/**
 * Hook to obtain a QCollab JWT for the current user via backend proxy.
 * The QOps backend handles the service token exchange — the frontend
 * never sees the QCollab service credentials.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';

interface CollabTokenResponse {
  token: string;
  expiresAt: number;
}

export function useCollabToken(enabled = true) {
  return useQuery<CollabTokenResponse>({
    queryKey: ['collab-token'],
    queryFn: () => api.get<CollabTokenResponse>('/collab/token'),
    enabled,
    // Refresh 60s before expiry
    staleTime: (query) => {
      const data = query.state.data;
      if (!data?.expiresAt) return 4 * 60 * 1000; // default 4min
      const msUntilExpiry = data.expiresAt * 1000 - Date.now();
      return Math.max(msUntilExpiry - 60_000, 0);
    },
    refetchOnWindowFocus: true,
    // Auto-refresh before token expires
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.expiresAt) return false;
      const msUntilExpiry = data.expiresAt * 1000 - Date.now();
      return msUntilExpiry > 120_000 ? msUntilExpiry - 60_000 : 30_000;
    },
    retry: 2,
  });
}
